/**
 * Resolves browser geolocation for hourly presence confirmation.
 * Uses a two-step strategy (network/cached first, then GPS) for fewer timeouts.
 */

const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

function coordsFromPosition(p) {
  return {
    latitude: p.coords.latitude,
    longitude: p.coords.longitude,
    accuracy: p.coords.accuracy
  };
}

function getCurrentPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve(coordsFromPosition(p)),
      (err) => reject(err),
      options
    );
  });
}

function mapErrorCode(code) {
  if (code === PERMISSION_DENIED) return 'permission_denied';
  if (code === POSITION_UNAVAILABLE) return 'unavailable';
  if (code === TIMEOUT) return 'timeout';
  return 'unknown';
}

/**
 * @returns {Promise<{ ok: true, position: { latitude, longitude, accuracy } } | { ok: false, reason: string }>}
 */
export async function fetchPositionForPresenceConfirm() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unsupported' };
  }

  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return { ok: false, reason: 'insecure_context' };
  }

  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'denied') {
        return { ok: false, reason: 'permission_denied' };
      }
    }
  } catch (_e) {
    /* continue — Permissions API unsupported for geolocation in some browsers */
  }

  const attempts = [
    { enableHighAccuracy: false, maximumAge: 180000, timeout: 20000 },
    { enableHighAccuracy: true, maximumAge: 120000, timeout: 35000 }
  ];

  let lastReason = 'unknown';

  for (let i = 0; i < attempts.length; i += 1) {
    try {
      const position = await getCurrentPosition(attempts[i]);
      return { ok: true, position };
    } catch (err) {
      const code = err && typeof err.code === 'number' ? err.code : 0;
      lastReason = mapErrorCode(code);
      if (code === PERMISSION_DENIED) {
        return { ok: false, reason: 'permission_denied' };
      }
    }
  }

  return { ok: false, reason: lastReason };
}

const REASON_TO_I18N = {
  unsupported: 'notifications.presence_geo_unsupported',
  insecure_context: 'notifications.presence_geo_insecure_context',
  permission_denied: 'notifications.presence_geo_permission_denied',
  unavailable: 'notifications.presence_geo_unavailable',
  timeout: 'notifications.presence_geo_timeout',
  unknown: 'notifications.presence_geo_failed'
};

/**
 * @param {(key: string) => string} t - i18n translate function
 * @param {{ ok: false, reason: string }} result
 */
export function messageForPresenceGeoFailure(t, result) {
  if (result.ok) return '';
  const key = REASON_TO_I18N[result.reason] || 'notifications.presence_confirm_location_required';
  const msg = t(key);
  if (msg && msg !== key) return msg;
  return (
    t('notifications.presence_confirm_location_required') || 'Could not read your location. Check browser permissions and try again.'
  );
}
